import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/**
 * Body for "Closing the loop: production logs in your AI IDE" guide.
 * The developer inner-loop use case for the MCP server: build in Claude Code,
 * Cursor or Codex, and let the same agent read production logs, verify
 * deploys and debug errors without leaving the editor.
 */
export function ProductionLogsAiIdeGuideBody() {
  return (
    <>
      <Lead>
        AI coding agents write and ship code faster than ever, but the feedback
        that tells you whether the code actually works still lives somewhere
        else: in dashboards, terminals and pasted stack traces. Connecting your
        log platform to Claude Code, Cursor or Codex over the Model Context
        Protocol (MCP) closes that loop. The agent that wrote the code can read
        the production logs it produces, verify its own deploy, and debug the
        errors it caused, in the same session.
      </Lead>

      <GuideSection id="the-broken-loop" title="The loop is broken">
        <P>
          A typical AI-assisted workflow today: you build a feature with an
          agent, deploy it, and then leave the editor. You open a logging
          dashboard, grep a container, or wait for an error tracker (or a user)
          to tell you something broke. Then you copy a stack trace, paste it
          back into the agent, and ask it to fix a bug it cannot see in
          context.
        </P>
        <P>
          The agent is blind exactly where it matters most. It knows every line
          of the code it wrote, but nothing about how that code behaves in
          production. Every round trip through a dashboard is manual glue
          between two halves of the same task: writing the code and observing
          it run.
        </P>
      </GuideSection>

      <GuideSection id="closing-the-loop" title="What closing the loop means">
        <P>
          <Term>Closing the loop</Term> means giving the coding agent the same
          feedback a senior engineer has: direct access to production logs,
          error patterns and service health. With that access, one session
          covers the whole cycle:
        </P>
        <OL>
          <LI>The agent writes the code and you deploy it.</LI>
          <LI>The agent queries production logs for new errors since the deploy.</LI>
          <LI>It reads the actual stack traces and correlates them with the change it just made.</LI>
          <LI>It writes the fix, you redeploy, and it verifies in the logs that the errors are gone.</LI>
        </OL>
        <P>
          No tab switching, no copy-pasting stack traces, no waiting for a bug
          report. The feedback loop that used to span tools and days runs
          inside the editor in minutes.
        </P>
      </GuideSection>

      <GuideSection id="how-mcp-works" title="How MCP puts production logs in your IDE">
        <P>
          <Term>MCP</Term> (Model Context Protocol) is the open protocol that
          standardises how AI agents connect to external systems. A log
          platform that ships an MCP server exposes <Term>typed tools</Term>{' '}
          the agent can call directly, instead of scraping a UI or hand-writing
          API calls. LogPulse&apos;s remote MCP server is one HTTPS endpoint
          that Claude Code, Cursor, Codex or any other MCP client connects to
          once; from then on the agent can call tools such as:
        </P>
        <UL>
          <LI><Term>search_logs</Term>: search production logs with a plain-language question or an LPQL query.</LI>
          <LI><Term>count_patterns</Term>: group errors into patterns and see which ones are new since a deploy.</LI>
          <LI><Term>compare_timeranges</Term>: compare before and after a release to spot regressions.</LI>
          <LI><Term>timeline_analysis</Term>: see when an error started and how it spread.</LI>
          <LI><Term>get_service_health</Term>: check whether latency, error rate and KPIs are back to normal after the fix.</LI>
        </UL>
        <P>
          Because the tools run inside the platform, answers come back in
          seconds, scoped to your tenant and your permissions. The agent
          reasons over real data instead of guessing from whatever you pasted.
          The full catalog and setup are on the{' '}
          <GuideLink href="/mcp">MCP server page</GuideLink> and in the{' '}
          <GuideLink href="/docs/integrations/mcp-server">documentation</GuideLink>.
        </P>
      </GuideSection>

      <GuideSection id="workflows" title="Four workflows that close the loop">
        <UL>
          <LI>
            <Term>Post-deploy verification</Term>: after every deploy, ask the
            agent to check production for new errors. It searches the logs
            since the release, compares error patterns against the previous
            window, and reports anything new, before your users do.
          </LI>
          <LI>
            <Term>Error-driven debugging</Term>: instead of pasting one stack
            trace, the agent pulls every occurrence of the error, the
            surrounding log lines, and the request context. It sees the
            failure the way production saw it, then fixes the code it already
            knows.
          </LI>
          <LI>
            <Term>Regression hunting</Term>: ask &quot;did anything get slower or
            noisier since v0.8?&quot; and the agent compares timeranges across
            releases, correlating error volume and latency with your changelog.
          </LI>
          <LI>
            <Term>From error to alert</Term>: once a bug class is fixed, the
            agent can draft an alert rule or detection for it, so the same
            failure never ships silently again. Drafts are queued for human
            approval; the agent never changes configuration directly.
          </LI>
        </UL>
      </GuideSection>

      <GuideSection id="vibe-coding" title="Vibe-coded apps need production feedback most">
        <P>
          The faster code is written, the more the balance of engineering
          shifts from writing it to operating it. Apps built primarily with AI
          agents (&quot;vibe coding&quot;) make this acute: the person shipping the app
          may never have read the code, and will not be reading raw log files
          or building Grafana dashboards either.
        </P>
        <P>
          The practical answer is to let the agent do the operating too. Ship
          logs from the app to a platform the agent can query over MCP, and
          &quot;why is my app broken?&quot; becomes a question the agent can answer with
          evidence: the actual errors, when they started, which deploy caused
          them, and what to change. Logging becomes the missing feedback organ
          of AI-built software, not an ops chore bolted on later.
        </P>
        <Callout title="Start logging before you need it">
          A vibe-coded app without logs is undebuggable for humans and agents
          alike. Adding structured logging is a one-prompt task for the same
          agent that builds the app; do it in the first session, not after the
          first outage.
        </Callout>
      </GuideSection>

      <GuideSection id="guardrails" title="Guardrails: give the agent read access, not the keys">
        <P>
          Connecting an autonomous agent to production data deserves the same
          care as onboarding a new engineer. The rules that make it safe:
        </P>
        <UL>
          <LI><Term>Read-only by default</Term>: the agent can search and analyse, not change anything.</LI>
          <LI><Term>Scoped tokens</Term>: a personal access token with only the scopes the workflow needs (for the dev loop, log search is usually enough).</LI>
          <LI><Term>Human-gated writes</Term>: anything the agent proposes (an alert rule, a pipeline) is queued as a disabled draft for review.</LI>
          <LI><Term>Audit trail</Term>: every tool call is logged, rate-limited and attributable to the token that made it.</LI>
        </UL>
        <P>
          The risks (prompt injection through log content, over-broad scopes)
          and how a well-designed MCP gateway mitigates them are covered in
          depth in{' '}
          <GuideLink href="/guides/mcp-for-security">
            MCP for security: connecting AI agents to your logs
          </GuideLink>
          .
        </P>
      </GuideSection>

      <GuideSection id="getting-started" title="Getting started">
        <OL>
          <LI>Ship your application logs to LogPulse (an HTTP endpoint, agent or OTLP; the <GuideLink href="/docs/getting-started/quickstart">quickstart</GuideLink> takes about five minutes).</LI>
          <LI>Connect your AI IDE to the LogPulse MCP server with one-click OAuth or a scoped personal access token.</LI>
          <LI>After your next deploy, ask the agent: &quot;check production for new errors since the deploy&quot;.</LI>
          <LI>Let it find, fix and verify. The loop is closed.</LI>
        </OL>
      </GuideSection>
    </>
  );
}
